import { useState } from 'react';
import { motion } from 'framer-motion';
import { Clock, Edit2, FileText, Plus, Save, Trash2, User } from 'lucide-react';
import type { ComiteAgendaItem, ComiteReunionComplete } from '../../services/comiteService';
import { addAgendaItem, updateAgendaItem, deleteAgendaItem } from '../../services/comiteService';

type ToastFn = (message: string, type: 'success' | 'error') => void;

interface AgendaTabProps {
  reunion: ComiteReunionComplete;
  onUpdate: () => void;
  showToast: ToastFn;
}

export default function AgendaTab({ reunion, onUpdate, showToast }: AgendaTabProps) {
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editData, setEditData] = useState<Partial<ComiteAgendaItem>>({});
  const [newItem, setNewItem] = useState<Partial<ComiteAgendaItem>>({
    titre: '',
    description: '',
    duree_estimee_min: 10,
    responsable: '',
    ordre: (reunion.agenda?.length || 0) + 1,
    statut: 'Non traité',
  });

  const totalDuree =
    reunion.agenda?.reduce((sum, item) => sum + (item.duree_estimee_min || 0), 0) || 0;

  const handleAdd = async () => {
    try {
      if (!reunion.id) return;
      if (!newItem.titre) return showToast('Le titre est requis', 'error');
      await addAgendaItem({ ...newItem, reunion_id: reunion.id } as any);
      showToast('Point ajouté!', 'success');
      setShowAddForm(false);
      setNewItem({
        titre: '',
        description: '',
        duree_estimee_min: 10,
        responsable: '',
        ordre: (reunion.agenda?.length || 0) + 2,
        statut: 'Non traité',
      });
      onUpdate();
    } catch (e) {
      console.error(e);
      showToast("Erreur lors de l'ajout", 'error');
    }
  };

  const startEdit = (item: ComiteAgendaItem) => {
    setEditingId(item.id || null);
    setEditData({
      titre: item.titre,
      description: item.description,
      duree_estimee_min: item.duree_estimee_min,
      responsable: item.responsable,
      statut: item.statut,
      ordre: item.ordre,
    });
  };

  const saveEdit = async () => {
    try {
      if (!editingId) return;
      if (!editData.titre) return showToast('Le titre est requis', 'error');
      await updateAgendaItem(editingId, editData);
      showToast('Point mis à jour!', 'success');
      setEditingId(null);
      setEditData({});
      onUpdate();
    } catch (e) {
      console.error(e);
      showToast('Erreur lors de la mise à jour', 'error');
    }
  };

  const remove = async (id: number, titre: string) => {
    if (!confirm(`Supprimer "${titre}"?`)) return;
    try {
      await deleteAgendaItem(id);
      showToast('Point supprimé!', 'success');
      onUpdate();
    } catch (e) {
      console.error(e);
      showToast('Erreur lors de la suppression', 'error');
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      className="space-y-6"
    >
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-2xl font-bold text-gray-800 flex items-center gap-3">
            <FileText className="w-7 h-7 text-green-600" />
            Ordre du Jour
          </h3>
          <p className="text-sm text-gray-600 mt-1">
            {reunion.agenda?.length || 0} point(s) • Durée totale: {totalDuree} minutes
          </p>
        </div>
        <button
          onClick={() => setShowAddForm(true)}
          className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
        >
          <Plus className="w-4 h-4" />
          Ajouter un point
        </button>
      </div>

      {showAddForm && (
        <div className="bg-green-50 border-2 border-green-300 rounded-xl p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Titre *</label>
            <input
              type="text"
              value={newItem.titre || ''}
              onChange={(e) => setNewItem({ ...newItem, titre: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
            <textarea
              rows={2}
              value={newItem.description || ''}
              onChange={(e) => setNewItem({ ...newItem, description: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Durée (min)</label>
              <input
                type="number"
                min={5}
                step={5}
                value={newItem.duree_estimee_min ?? 10}
                onChange={(e) =>
                  setNewItem({ ...newItem, duree_estimee_min: parseInt(e.target.value) })
                }
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Responsable</label>
              <input
                type="text"
                value={newItem.responsable || ''}
                onChange={(e) => setNewItem({ ...newItem, responsable: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
              />
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={handleAdd} className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700">
              Ajouter
            </button>
            <button onClick={() => setShowAddForm(false)} className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300">
              Annuler
            </button>
          </div>
        </div>
      )}

      {reunion.agenda && reunion.agenda.length > 0 ? (
        <div className="space-y-3">
          {reunion.agenda.map((item) => {
            const isEditing = editingId === item.id;
            return (
              <div key={item.id} className="bg-white border-2 border-gray-200 rounded-lg p-4">
                {isEditing ? (
                  <div className="space-y-3">
                    <input
                      value={editData.titre || ''}
                      onChange={(e) => setEditData({ ...editData, titre: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                    />
                    <textarea
                      rows={2}
                      value={editData.description || ''}
                      onChange={(e) => setEditData({ ...editData, description: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                    />
                    <div className="flex items-center gap-2">
                      <button onClick={saveEdit} className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700">
                        <Save className="w-4 h-4" />
                        Sauvegarder
                      </button>
                      <button onClick={() => { setEditingId(null); setEditData({}); }} className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300">
                        Annuler
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1">
                      <div className="font-bold text-gray-800">{item.titre}</div>
                      {item.description && <div className="text-sm text-gray-600 mt-1">{item.description}</div>}
                      <div className="flex items-center gap-4 text-sm text-gray-600 mt-2">
                        {!!item.duree_estimee_min && (
                          <span className="flex items-center gap-1">
                            <Clock className="w-4 h-4" />
                            {item.duree_estimee_min} min
                          </span>
                        )}
                        {!!item.responsable && (
                          <span className="flex items-center gap-1">
                            <User className="w-4 h-4" />
                            {item.responsable}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      <button onClick={() => startEdit(item)} className="p-2 rounded-lg hover:bg-green-100 text-green-600" title="Modifier">
                        <Edit2 className="w-4 h-4" />
                      </button>
                      {item.id && (
                        <button onClick={() => remove(item.id!, item.titre)} className="p-2 rounded-lg hover:bg-red-100 text-red-600" title="Supprimer">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        <div className="text-center py-12 bg-gray-50 rounded-xl">
          <FileText className="w-16 h-16 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500">Aucun point à l'agenda</p>
        </div>
      )}
    </motion.div>
  );
}

